/**
 * Android Bridge Service
 *
 * Provides interface to communicate with Android native code
 * via the JavaScript bridge injected as window.Android
 */

// Extend Window interface to include Android bridge
declare global {
  interface Window {
    Android?: {
      startVivaPayment(amountCents: number, sessionId: string, transactionId: string): void;
      isVivaAppInstalled(): boolean;
      log(message: string): void;
    };
    onVivaPaymentResult?: (result: VivaPaymentResult) => void;
  }
}

export interface VivaPaymentResult {
  status: string;
  transactionId?: string;
  sessionId?: string;
  error?: string;
}

class AndroidBridgeService {
  private paymentCallbacks: Map<string, (result: VivaPaymentResult) => void> = new Map();

  constructor() {
    console.log('[AndroidBridge] Constructor called, isAndroidApp:', this.isAndroidApp());

    // Set up global callback handler for Android to call
    if (this.isAndroidApp()) {
      window.onVivaPaymentResult = (result: VivaPaymentResult) => {
        console.log('[AndroidBridge] Payment result received:', result);
        console.log('[AndroidBridge] Current registered callbacks:', Array.from(this.paymentCallbacks.keys()));

        // Store result in localStorage to survive page reloads
        localStorage.setItem('vivaPaymentResult', JSON.stringify(result));
        console.log('[AndroidBridge] Stored result in localStorage');

        // Find and call the registered callback for this session
        const callback = this.paymentCallbacks.get(result.sessionId || '');
        if (callback) {
          console.log('[AndroidBridge] Callback found, executing...');
          callback(result);
          this.paymentCallbacks.delete(result.sessionId || '');
        } else {
          console.error('[AndroidBridge] No callback registered for session:', result.sessionId);
          console.error('[AndroidBridge] Available sessions:', Array.from(this.paymentCallbacks.keys()));
          console.error('[AndroidBridge] Result stored in localStorage for retrieval after reload');
        }
      };
      console.log('[AndroidBridge] window.onVivaPaymentResult installed');
    }
  }

  /**
   * Check if running in Android WebView
   */
  isAndroidApp(): boolean {
    return typeof window.Android !== 'undefined';
  }

  /**
   * Check if Viva Wallet app is installed on the device
   */
  isVivaAppInstalled(): boolean {
    if (!this.isAndroidApp()) {
      console.warn('[AndroidBridge] Not running in Android app');
      return false;
    }

    try {
      return window.Android!.isVivaAppInstalled();
    } catch (error) {
      console.error('[AndroidBridge] Error checking Viva app:', error);
      return false;
    }
  }

  /**
   * Check for pending payment result in localStorage (survives page reload)
   */
  checkPendingPaymentResult(): VivaPaymentResult | null {
    try {
      const stored = localStorage.getItem('vivaPaymentResult');
      if (stored) {
        console.log('[AndroidBridge] Found pending payment result in localStorage');
        const result = JSON.parse(stored) as VivaPaymentResult;
        localStorage.removeItem('vivaPaymentResult'); // Clear it
        return result;
      }
    } catch (error) {
      console.error('[AndroidBridge] Error checking pending result:', error);
    }
    return null;
  }

  /**
   * Start Viva Wallet payment via Android Intent
   *
   * @param amountCents - Amount in cents (e.g., 100 for 1.00)
   * @param sessionId - Unique session identifier
   * @param transactionId - Transaction ID from backend
   * @returns Promise that resolves when payment completes
   */
  startVivaPayment(
    amountCents: number,
    sessionId: string,
    transactionId: string
  ): Promise<VivaPaymentResult> {
    console.log('[AndroidBridge] startVivaPayment called');

    // Check if there's a pending result from before reload (match by transactionId)
    const pendingResult = this.checkPendingPaymentResult();
    if (pendingResult && pendingResult.transactionId?.includes(transactionId)) {
      console.log('[AndroidBridge] Returning pending result immediately for txn:', transactionId);
      return Promise.resolve(pendingResult);
    }

    return new Promise((resolve, reject) => {
      const isAndroid = this.isAndroidApp();
      console.log('[AndroidBridge] isAndroidApp:', isAndroid);

      if (!isAndroid) {
        console.error('[AndroidBridge] Rejecting: Not running in Android app');
        reject(new Error('Not running in Android app'));
        return;
      }

      const vivaInstalled = this.isVivaAppInstalled();
      console.log('[AndroidBridge] isVivaAppInstalled:', vivaInstalled);

      if (!vivaInstalled) {
        console.error('[AndroidBridge] Rejecting: Viva Wallet app is not installed');
        reject(new Error('Viva Wallet app is not installed'));
        return;
      }

      console.log('[AndroidBridge] Starting Viva payment:', {
        amountCents,
        sessionId,
        transactionId
      });

      // Register callback for this payment
      this.paymentCallbacks.set(sessionId, (result: VivaPaymentResult) => {
        console.log('[AndroidBridge] Payment completed:', result);

        if (result.status === 'ERROR') {
          reject(new Error(result.error || 'Payment failed'));
        } else {
          resolve(result);
        }
      });

      // Call Android method to start payment
      try {
        window.Android!.startVivaPayment(amountCents, sessionId, transactionId);
      } catch (error) {
        console.error('[AndroidBridge] Error starting payment:', error);
        this.paymentCallbacks.delete(sessionId);
        reject(error);
      }
    });
  }

  /**
   * Log message to Android logcat (for debugging)
   */
  log(message: string): void {
    if (this.isAndroidApp()) {
      try {
        window.Android!.log(`[Kiosk] ${message}`);
      } catch (error) {
        console.error('[AndroidBridge] Error logging to Android:', error);
      }
    }
  }

  /**
   * Get payment method capabilities
   */
  getPaymentCapabilities(): { supportsVivaIntent: boolean } {
    const isAndroid = this.isAndroidApp();
    const vivaInstalled = isAndroid && this.isVivaAppInstalled();

    return {
      supportsVivaIntent: vivaInstalled,
    };
  }
}

// Export singleton instance
export const androidBridge = new AndroidBridgeService();

export default androidBridge;
